/*
 * Customer videos (UGC) — ported from the Nuvola theme (assets/nuvola-reference-product.js).
 * Tiles play muted while at least 60% on screen; one tile at a time can have sound.
 * A tap opens a full-screen viewer: the tiles' own <video> elements move in (no second download),
 * swipe / arrows / ←→ to step, tap to pause, swipe up or down to close.
 */
if (!customElements.get('ap-ugc')) customElements.define('ap-ugc', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const opts = { signal: this.abort.signal };
    const focusQuietly = (el, byPointer) => {
      if (byPointer) {
        el.dataset.quietFocus = '';
        const clear = () => delete el.dataset.quietFocus;
        el.addEventListener('blur', clear, { once: true });
        el.addEventListener('keydown', clear, { once: true });
      }
      el.focus({ preventScroll: true });
    };
    const clips = [...this.querySelectorAll('.ap-ugc__item video')];
    const sounds = [...this.querySelectorAll('[data-ugc-sound]')];
    if (!clips.length) return;

    // covers load as the block nears the screen, and in any case shortly after page load
    const cover = (v) => { if (v.dataset.poster && !v.getAttribute('poster')) v.poster = v.dataset.poster; };
    if ('IntersectionObserver' in window) {
      this.coverWatch = new IntersectionObserver((es) => {
        if (es.some((e) => e.isIntersecting)) { clips.forEach(cover); this.coverWatch.disconnect(); }
      }, { rootMargin: '600px 0px' });
      this.coverWatch.observe(this);
    } else clips.forEach(cover);
    const ready = () => { clips.forEach(cover); clips.slice(0, 2).forEach((v) => { if (v.preload === 'none') v.preload = 'metadata'; }); };
    if (document.readyState === 'complete') setTimeout(ready, 1500);
    else addEventListener('load', () => setTimeout(ready, 1500), { ...opts, once: true });

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      clips.forEach((v) => { v.controls = true; v.preload = 'metadata'; });
      sounds.forEach((b) => (b.hidden = true));
    } else {
      clips.forEach((v) => { v.muted = true; v.removeAttribute('controls'); });
      this.clipWatch = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.intersectionRatio >= 0.6 && !this.viewerOpen) e.target.play().catch(() => {});
        else e.target.pause();
      }), { threshold: [0, 0.6] });
      clips.forEach((v) => this.clipWatch.observe(v));
    }
    sounds.forEach((btn) => btn.addEventListener('click', () => {
      const clip = btn.parentElement.querySelector('video'), turnOn = clip.muted;
      sounds.forEach((b) => {
        b.parentElement.querySelector('video').muted = true;
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', 'Включить звук');
      });
      if (turnOn) {
        clip.muted = false; clip.play().catch(() => {});
        btn.setAttribute('aria-pressed', 'true'); btn.setAttribute('aria-label', 'Выключить звук');
      }
    }, opts));
    const viewer = this.querySelector('[data-ugc-viewer]');
    if (viewer) this.ugcViewer(viewer, clips, sounds, opts, focusQuietly);
  }

  ugcViewer(viewer, clips, sounds, opts, focusQuietly) {
    const track = viewer.querySelector('[data-ugc-track]'), count = viewer.querySelector('[data-ugc-count]');
    const soundBtn = viewer.querySelector('[data-ugc-viewer-sound]'), closeBtn = viewer.querySelector('[data-ugc-close]');
    const steps = [...viewer.querySelectorAll('[data-ugc-step]')], openers = [...this.querySelectorAll('[data-ugc-open]')];
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tiles = clips.map((v) => v.closest('li')), anchors = clips.map((v) => v.nextSibling);
    const posters = clips.map((v) => v.getAttribute('poster') || v.dataset.poster || '');
    let slides = [], active = -1, muted = false, opener = null, byPointer = false, controls = [];
    const setSound = () => {
      soundBtn.setAttribute('aria-pressed', String(!muted));
      soundBtn.setAttribute('aria-label', muted ? 'Включить звук' : 'Выключить звук');
    };
    const build = () => {
      slides = clips.map((v, i) => {
        const slide = document.createElement('div'); slide.className = 'ap-ugc-viewer__slide';
        slide.setAttribute('role', 'group'); slide.setAttribute('aria-roledescription', 'видео'); slide.setAttribute('aria-label', `${i + 1} из ${clips.length}`);
        const play = document.createElement('span'); play.className = 'ap-ugc-viewer__play'; play.setAttribute('aria-hidden', 'true');
        play.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
        const spin = document.createElement('span'); spin.className = 'ap-ugc-viewer__spinner'; spin.setAttribute('aria-hidden', 'true');
        const bar = document.createElement('span'); bar.className = 'ap-ugc-viewer__progress'; bar.setAttribute('aria-hidden', 'true'); bar.append(document.createElement('span'));
        slide.append(play, spin, bar);
        const caption = tiles[i] && tiles[i].querySelector('.ap-ugc__caption');
        if (caption) { const p = document.createElement('p'); p.className = 'ap-ugc-viewer__caption'; p.textContent = caption.textContent; slide.append(p); }
        const inViewer = () => v.parentElement === slide;
        v.addEventListener('click', () => { if (!inViewer()) return; if (v.paused) v.play().catch(() => {}); else v.pause(); }, opts);
        v.addEventListener('play', () => slide.classList.remove('is-paused'), opts);
        v.addEventListener('pause', () => { if (inViewer()) slide.classList.add('is-paused'); }, opts);
        v.addEventListener('waiting', () => { if (inViewer()) slide.classList.add('is-loading'); }, opts);
        v.addEventListener('playing', () => {
          slide.classList.remove('is-loading');
          if (inViewer() && slides[active] === slide) [active - 1, active + 1].forEach((j) => { if (clips[j] && clips[j].preload === 'none') clips[j].preload = 'metadata'; });
        }, opts);
        v.addEventListener('timeupdate', () => { if (v.duration) bar.firstChild.style.width = `${(v.currentTime / v.duration) * 100}%`; }, opts);
        track.append(slide);
        return slide;
      });
      this.slideWatch = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.intersectionRatio >= 0.6) show(slides.indexOf(e.target));
      }), { root: track, threshold: [0.6] });
      slides.forEach((sl) => this.slideWatch.observe(sl));
    };
    const show = (i) => {
      if (i < 0 || i >= slides.length || !viewer.open) return;
      if (i !== active) {
        slides.forEach((sl, j) => { if (j !== i) clips[j].pause(); });
        active = i; count.textContent = `${i + 1} / ${slides.length}`;
        steps.forEach((b) => { b.disabled = b.dataset.ugcStep < 0 ? i === 0 : i === slides.length - 1; });
      }
      const v = clips[i]; v.muted = muted;
      slides[i].classList.toggle('is-loading', v.readyState < 3);
      if (v.paused) v.play().catch(() => { if (!v.muted) { v.muted = muted = true; setSound(); v.play().catch(() => {}); } });
    };
    const go = (i) => {
      if (i < 0 || i >= slides.length) return;
      show(i);
      track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? 'instant' : 'smooth' });
    };
    const open = (i, from, pointer) => {
      if (!slides.length) build();
      opener = from; byPointer = pointer; this.viewerOpen = true;
      sounds.forEach((b) => { b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', 'Включить звук'); });
      if (this.clipWatch) clips.forEach((v) => this.clipWatch.unobserve(v));
      controls = clips.map((v) => v.controls);
      clips.forEach((v, j) => {
        v.muted = true; v.controls = false;
        if (posters[j]) v.poster = posters[j].replace(/([?&]width=)\d+/, '$11080');
        if (posters[j]) tiles[j].style.backgroundImage = `url("${posters[j]}")`;
        slides[j].prepend(v);
      });
      muted = false; setSound(); active = -1;
      document.documentElement.classList.add('ap-ugc-lock');
      viewer.showModal();
      track.scrollLeft = i * track.clientWidth;
      show(i);
      focusQuietly(closeBtn, pointer);
    };
    openers.forEach((btn, i) => btn.addEventListener('click', (e) => open(i, btn, e.detail > 0), opts));
    steps.forEach((b) => b.addEventListener('click', () => go(active + Number(b.dataset.ugcStep)), opts));
    soundBtn.addEventListener('click', () => {
      muted = !muted; setSound();
      if (active >= 0) { const v = clips[active]; v.muted = muted; if (v.paused) v.play().catch(() => {}); }
    }, opts);
    closeBtn.addEventListener('click', (e) => { byPointer = e.detail > 0; viewer.close(); }, opts);
    viewer.addEventListener('cancel', () => { byPointer = false; }, opts);
    viewer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
    }, opts);
    addEventListener('resize', () => { if (viewer.open && active >= 0) track.scrollLeft = active * track.clientWidth; }, opts);

    // swipe up/down closes; sideways stays the track's own scroll
    let sx = 0, sy = null, drag = false, trail = [];
    const settle = () => { track.style.transition = ''; track.style.transform = ''; viewer.style.removeProperty('--ap-ugc-fade'); };
    track.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { sy = null; return; }
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; drag = false; trail = [{ y: sy, t: e.timeStamp }];
    }, { ...opts, passive: true });
    track.addEventListener('touchmove', (e) => {
      if (sy == null) return;
      const dx = e.touches[0].clientX - sx, dy = e.touches[0].clientY - sy;
      trail.push({ y: e.touches[0].clientY, t: e.timeStamp });
      while (trail.length > 2 && e.timeStamp - trail[0].t > 100) trail.shift();
      if (!drag) { if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx) * 1.2) drag = true; else { if (Math.abs(dx) > 12) sy = null; return; } }
      track.style.transition = 'none'; track.style.transform = `translateY(${dy}px)`;
      viewer.style.setProperty('--ap-ugc-fade', String(1 - Math.min(Math.abs(dy) / 500, 0.6)));
    }, { ...opts, passive: true });
    track.addEventListener('touchend', (e) => {
      if (sy == null || !drag) { sy = null; return; }
      const y = e.changedTouches[0].clientY, dy = y - sy, from = trail[0];
      const speed = Math.abs(y - from.y) / Math.max(16, e.timeStamp - from.t);
      sy = null; drag = false;
      if (Math.abs(dy) > 90 || speed > 0.5) {
        byPointer = true;
        if (reduce) { viewer.close(); return; }
        track.style.transition = 'transform .2s ease-in'; track.style.transform = `translateY(${Math.sign(dy) * innerHeight}px)`;
        viewer.style.setProperty('--ap-ugc-fade', '0');
        setTimeout(() => { if (viewer.open) viewer.close(); }, 200);
      } else { track.style.transition = 'transform .2s ease-out'; track.style.transform = ''; viewer.style.removeProperty('--ap-ugc-fade'); }
    }, opts);
    track.addEventListener('touchcancel', () => { sy = null; drag = false; settle(); }, opts);
    viewer.addEventListener('close', () => {
      settle();
      document.documentElement.classList.remove('ap-ugc-lock');
      this.viewerOpen = false; active = -1;
      clips.forEach((v, j) => {
        v.pause(); v.muted = true; v.controls = controls[j];
        if (posters[j]) v.poster = posters[j];
        tiles[j].insertBefore(v, anchors[j]); tiles[j].style.backgroundImage = '';
        slides[j].classList.remove('is-paused', 'is-loading');
      });
      if (this.clipWatch) clips.forEach((v) => this.clipWatch.observe(v));
      if (opener) focusQuietly(opener, byPointer);
    }, opts);
  }

  disconnectedCallback() {
    if (this.coverWatch) this.coverWatch.disconnect();
    if (this.slideWatch) this.slideWatch.disconnect();
    if (this.clipWatch) this.clipWatch.disconnect();
    if (this.abort) this.abort.abort();
  }
});
